import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Skeleton,   // ✅ import Skeleton
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { useNavigate } from "react-router-dom";
import AddWorkOrderSmart from "./addWorkorder";
import * as MuiIcons from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import api from "../../../src/utils/api";

const ITEMS_PER_PAGE = 5;

const WorkOrders = ({ workOrders, propertyId, openerUserId, loading }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [statuses, setStatuses] = useState([]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setPage(1);
  };

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await api.get(`/statuses`);
        setStatuses(response.data);
      } catch (error) {
        console.error("Failed to fetch statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [sortOrder, searchQuery]);

  const getStatusInfo = (statusName) =>
    statuses.find((s) => s.statusname === statusName);

  const getOrderOpenerId = (order) =>
    order?.opened_by_user_id ??
    order?.opener_user_id ??
    order?.openedByUserId ??
    order?.openerUserId ??
    order?.created_by ??
    order?.user_id ??
    null;

  const displayWorkOrders = useMemo(() => {
    const prop = propertyId?.toString().trim();
    if (prop === "008000" && openerUserId != null) {
      const target = Number(openerUserId);
      if (!Number.isFinite(target)) return workOrders;
      return workOrders.filter((wo) => {
        const opener = getOrderOpenerId(wo);
        return opener != null && Number(opener) === target;
      });
    }
    return workOrders;
  }, [workOrders, propertyId, openerUserId]);

  const searchedWorkOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return displayWorkOrders;
    return displayWorkOrders.filter((order) => {
      const idMatch = order.order_id?.toString().toLowerCase().includes(q);
      const descMatch = order.description?.toLowerCase().includes(q);
      return idMatch || descMatch;
    });
  }, [displayWorkOrders, searchQuery]);

  const sortedWorkOrders = useMemo(() => {
    return [...searchedWorkOrders].sort((a, b) => {
      const dateA = new Date(a.created_at || a.updated_at || a.order_id);
      const dateB = new Date(b.created_at || b.updated_at || b.order_id);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [searchedWorkOrders, sortOrder]);

  const paginatedWorkOrders = useMemo(() => {
    return sortedWorkOrders.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  }, [sortedWorkOrders, page]);

  const resolvedCount = useMemo(
    () => displayWorkOrders.filter((order) => order.resolved_at).length,
    [displayWorkOrders]
  );

  const renderStatusText = (order) => {
    const statusInfo = getStatusInfo(order.status);
    const IconComponent = statusInfo?.icon && MuiIcons[statusInfo.icon];
    const themeColor =
      statusInfo?.color && theme.palette[statusInfo.color]
        ? theme.palette[statusInfo.color].main
        : statusInfo?.color || "#555";

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        {IconComponent && <IconComponent fontSize="small" sx={{ color: themeColor }} />}
        <Typography sx={{ color: themeColor, fontWeight: "bold" }}>
          {statusInfo?.statusname || order.status || "Unknown"}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {/* Loading Skeletons */}
      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                <Skeleton variant="text" width="60%" height={30} />
                <Skeleton variant="text" width="40%" height={25} />
                <Skeleton variant="rectangular" height={40} sx={{ mt: 1, borderRadius: 1 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {/* Add Work Order Modal */}
          <AddWorkOrderSmart
            open={openAddModal}
            handleClose={() => setOpenAddModal(false)}
            propertyId={propertyId}
            onWorkOrderAdded={() => {
              setShowSuccessSnackbar(true);
            }}
            uploadedBy={openerUserId ?? 1}
            openedByUserId={openerUserId ?? null}
          />

          {/* Success Snackbar */}
          <Snackbar
            open={showSuccessSnackbar}
            autoHideDuration={3000}
            onClose={() => setShowSuccessSnackbar(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity="success"
              onClose={() => setShowSuccessSnackbar(false)}
              sx={{ width: "100%" }}
            >
              Work order created successfully!
            </Alert>
          </Snackbar>

          {/* Work Orders Header Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                Total
              </Typography>
              <Typography variant="h5" color="error">
                {displayWorkOrders.length}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                Resolved
              </Typography>
              <Typography variant="h5" color="success.main">
                {resolvedCount}
              </Typography>
            </Grid>
          </Grid>

          {/* Sort and Search */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mb: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="sort-label">Sort By</InputLabel>
              <Select
                labelId="sort-label"
                value={sortOrder}
                label="Sort By"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Search by ID or Description"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
          </Box>

          {/* Main List */}
          {sortedWorkOrders.length === 0 ? (
            <Box sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
              <Typography variant="body1">No matching work orders found.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {sortedWorkOrders.slice(0, 5).map((order) => {
                const statusInfo = getStatusInfo(order.status);
                const borderColor =
                  theme.palette[statusInfo?.color]?.main || statusInfo?.color || "#ccc";

                return (
                  <Grid item xs={12} md={6} key={order.order_id}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: 2,
                        boxShadow: 1,
                        borderLeft: `5px solid ${borderColor}`,
                      }}
                    >
                      <Typography>
                        <strong>ID:</strong> WO-{order.order_id}
                      </Typography>
                      <Typography>
                        <strong>Title:</strong> {order.description || "No description"}
                      </Typography>
                      {renderStatusText(order)}
                      <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            navigate(`/home/memberspage/workorder/${order.order_id}`)
                          }
                        >
                          View Details
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              mt: 4,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="success"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setOpenAddModal(true)}
            >
              Create New Work Order
            </Button>

            {displayWorkOrders.length > 5 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AssignmentTurnedInIcon />}
                onClick={handleOpen}
              >
                View All
              </Button>
            )}
          </Box>

          {/* Dialog with All Work Orders */}
          <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>All Work Orders</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {paginatedWorkOrders.map((order) => {
                  const statusInfo = getStatusInfo(order.status);
                  const borderColor =
                    theme.palette[statusInfo?.color]?.main || statusInfo?.color || "#ccc";

                  return (
                    <Grid item xs={12} key={order.order_id}>
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: "#f8f9fa",
                          borderRadius: 2,
                          boxShadow: 1,
                          borderLeft: `5px solid ${borderColor}`,
                        }}
                      >
                        <Typography>
                          <strong>ID:</strong> WO-{order.order_id}
                        </Typography>
                        <Typography>
                          <strong>Title:</strong> {order.description || "No description"}
                        </Typography>
                        {renderStatusText(order)}
                        <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              handleClose();
                              navigate(`/home/memberspage/workorder/${order.order_id}`);
                            }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "center", flexDirection: "column", gap: 1 }}
            >
              <Pagination
                count={Math.ceil(sortedWorkOrders.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default WorkOrders;
